import React, { useState, useEffect } from 'react';
import { promotionsAPI, bijouxAPI } from '../../services/api';
import { Tag, Plus, X, Save, Edit, Trash2, Calendar, Percent, DollarSign, TrendingDown } from 'lucide-react';

function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [bijoux, setBijoux] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    type_reduction: 'pourcentage',
    valeur: '',
    bijou_id: '',
    categorie: '',
    date_debut: '',
    date_fin: '',
    actif: true
  });

  const categories = ['bague', 'collier', 'bracelet', 'boucles_oreilles', 'pendentif'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [promosRes, bijouxRes] = await Promise.all([
        promotionsAPI.getAll(),
        bijouxAPI.getAll()
      ]);
      setPromotions(promosRes.data);
      setBijoux(bijouxRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        valeur: parseFloat(formData.valeur),
        bijou_id: formData.bijou_id || null,
        categorie: formData.categorie || null
      };

      if (editingPromo) {
        await promotionsAPI.update(editingPromo.id, data);
      } else {
        await promotionsAPI.create(data);
      }

      setShowModal(false);
      setEditingPromo(null);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      nom: promo.nom,
      description: promo.description || '',
      type_reduction: promo.type_reduction,
      valeur: promo.valeur,
      bijou_id: promo.bijou_id || '',
      categorie: promo.categorie || '',
      date_debut: promo.date_debut || '',
      date_fin: promo.date_fin || '',
      actif: promo.actif
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette promotion ?')) {
      try {
        await promotionsAPI.delete(id);
        await loadData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      type_reduction: 'pourcentage',
      valeur: '',
      bijou_id: '',
      categorie: '',
      date_debut: '',
      date_fin: '',
      actif: true
    });
  };

  const getPromoBadge = (promo) => {
    const now = new Date();
    const debut = promo.date_debut ? new Date(promo.date_debut) : null;
    const fin = promo.date_fin ? new Date(promo.date_fin) : null;

    if (!promo.actif) return 'bg-gray-100 text-gray-800';
    if (debut && debut > now) return 'bg-blue-100 text-blue-800';
    if (fin && fin < now) return 'bg-red-100 text-red-800';
    return 'bg-green-100 text-green-800';
  };

  const getPromoStatus = (promo) => {
    const now = new Date();
    const debut = promo.date_debut ? new Date(promo.date_debut) : null;
    const fin = promo.date_fin ? new Date(promo.date_fin) : null;

    if (!promo.actif) return 'Inactive';
    if (debut && debut > now) return 'À venir';
    if (fin && fin < now) return 'Expirée';
    return 'Active';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
          <Tag className="h-8 w-8 text-purple-600" />
          <span>Gestion des Promotions</span>
        </h2>
        <button
          onClick={() => {
            resetForm();
            setEditingPromo(null);
            setShowModal(true);
          }}
          className="w-full sm:w-auto justify-center bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center space-x-2 shadow-lg"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvelle promotion</span>
        </button>
      </div>

      {/* Liste des promotions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {promotions.map((promo) => (
          <div key={promo.id} className="bg-white rounded-lg shadow-md overflow-hidden border-t-4 border-purple-500">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{promo.nom}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPromoBadge(promo)}`}>
                    {getPromoStatus(promo)}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(promo)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(promo.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {promo.description && (
                <p className="text-sm text-gray-600 mb-4">{promo.description}</p>
              )}

              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-lg font-bold text-purple-600">
                  {promo.type_reduction === 'pourcentage' ? (
                    <>
                      <Percent className="h-5 w-5" />
                      <span>-{promo.valeur}%</span>
                    </>
                  ) : (
                    <>
                      <DollarSign className="h-5 w-5" />
                      <span>-{promo.valeur} DH</span>
                    </>
                  )}
                </div>

                {promo.bijou_nom && (
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">Bijou:</span> {promo.bijou_nom} ({promo.reference})
                  </div>
                )}

                {promo.categorie && !promo.bijou_id && (
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">Catégorie:</span> {promo.categorie}
                  </div>
                )}

                {(promo.date_debut || promo.date_fin) && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mt-3 pt-3 border-t">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {promo.date_debut && new Date(promo.date_debut).toLocaleDateString('fr-FR')}
                      {promo.date_debut && promo.date_fin && ' → '}
                      {promo.date_fin && new Date(promo.date_fin).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Tag className="h-6 w-6" />
                <h3 className="text-xl font-bold">
                  {editingPromo ? 'Modifier la promotion' : 'Nouvelle promotion'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPromo(null);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la promotion *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de réduction *</label>
                  <select
                    value={formData.type_reduction}
                    onChange={(e) => setFormData({...formData, type_reduction: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="pourcentage">Pourcentage (%)</option>
                    <option value="montant">Montant fixe (DH)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur * {formData.type_reduction === 'pourcentage' ? '(%)' : '(DH)'}
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    value={formData.valeur}
                    onChange={(e) => setFormData({...formData, valeur: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bijou spécifique (optionnel)</label>
                  <select
                    value={formData.bijou_id}
                    onChange={(e) => setFormData({...formData, bijou_id: e.target.value, categorie: ''})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Aucun (tous les bijoux)</option>
                    {bijoux.map(bijou => (
                      <option key={bijou.id} value={bijou.id}>
                        {bijou.reference} - {bijou.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ou catégorie (optionnel)</label>
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({...formData, categorie: e.target.value, bijou_id: ''})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    disabled={formData.bijou_id !== ''}
                  >
                    <option value="">Aucune</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début (optionnel)</label>
                  <input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin (optionnel)</label>
                  <input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="actif"
                  checked={formData.actif}
                  onChange={(e) => setFormData({...formData, actif: e.target.checked})}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="actif" className="text-sm font-medium text-gray-700">
                  Promotion active
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPromo(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center space-x-2 disabled:opacity-50"
                >
                  <Save className="h-5 w-5" />
                  <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Promotions;
